import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";
import { getCart, invalidateCartCache } from "@theme/cart-state";
import {
  NUGGET_ONE_TIME_VARIANT,
  addNuggetOneTimeToCart,
  bundleState,
} from "@theme/nugget-bundle";

/**
 * Custom element for the nugget product card with bundle flow.
 *
 * @typedef {object} Refs
 * @property {import('@theme/dialog').DialogComponent} bundleDialogComponent - The nested dialog component
 * @property {HTMLElement} confirmationView - The confirmation view element
 *
 * @extends {Component<Refs>}
 */
class NuggetProductCard extends Component {
  get productId() {
    return this.dataset.productId;
  }

  get variantId() {
    return this.dataset.variantId;
  }

  /**
   * The purchase option currently selected in the card's toggle.
   * @returns {"bundle" | "standalone"}
   */
  get mode() {
    const checked = this.querySelector(
      'input[name^="nugget-mode"]:checked',
    );

    return checked instanceof HTMLInputElement && checked.value === "standalone"
      ? "standalone"
      : "bundle";
  }

  /**
   * Routes the primary CTA to the bundle flow or a direct standalone add,
   * depending on the selected purchase option.
   */
  handlePrimaryAction() {
    if (this.mode === "standalone") {
      this.addStandaloneToCart();
      return;
    }

    this.openBundlePopup();
  }

  /**
   * Adds the one-time (standalone) nugget variant straight to the cart.
   * No box is required, so the bundle flow is skipped entirely.
   */
  async addStandaloneToCart() {
    if (bundleState.bundleFlow) {
      bundleState.setBundleFlow(false, "nugget-standalone");
    }

    try {
      await addNuggetOneTimeToCart();

      // cart-icon treats non-product-form events without a cart resource as an
      // absolute count, so dispatching {} + itemCount: 1 would stamp the badge
      // to 1. Fetch the post-add cart and dispatch it as the resource instead.
      invalidateCartCache();
      const cart = await getCart({ force: true }).catch(() => null);

      this.dispatchEvent(
        new CartAddEvent(cart ?? {}, this.id || "nugget-product-card", {
          source: "nugget-product-card",
          productId: this.productId,
          variantId: String(NUGGET_ONE_TIME_VARIANT),
          itemCount: cart?.item_count ?? 1,
        }),
      );
    } catch (error) {
      console.error("Failed to add standalone nugget to cart:", error);
    }
  }

  /**
   * Opens the bundle popup dialog and shows the confirmation view.
   */
  openBundlePopup() {
    // Open the dialog
    this.refs.bundleDialogComponent?.showDialog();

    // Track analytics
    requestAnimationFrame(() => {
      this.#trackModalView("confirmation");
    });
  }

  /**
   * Closes the bundle popup dialog.
   * @returns {Promise<void>}
   */
  closeBundlePopup() {
    return this.refs.bundleDialogComponent?.closeDialog() ?? Promise.resolve();
  }

  /**
   * Requests the universal cancellation modal (handled by the bundle banner).
   * Used when user closes the confirmation dialog.
   */
  async requestCancellation() {
    await this.closeBundlePopup();
    this.dispatchEvent(
      new CustomEvent("bundle:show-cancellation", { bubbles: true }),
    );
  }

  /**
   * Starts the nugget bundle flow
   */
  async startBundleFlow() {
    await this.closeBundlePopup();
    bundleState.setBundleFlow(true, this.id);
    this.#trackBundleFlowEntry();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  // TODO: Implement Analytics Tracking

  /**
   * @param {"confirmation" | "cancellation"} viewType
   */
  #trackModalView(viewType) {
    if (viewType === "confirmation") {
      window.grAnalytics?.trackBundleSuccessModalViewed({});
      return;
    }

    window.grAnalytics?.trackBundleWarningModalViewed({});
  }

  #trackBundleFlowEntry() {
    window.grAnalytics?.trackBundleFlowEntry({});
  }

}

if (!customElements.get("nugget-product-card")) {
  customElements.define("nugget-product-card", NuggetProductCard);
}

export { NuggetProductCard };
